import {
  QueryDatasetDocument,
  SortDirection,
  useRunExportJobMutation,
} from '@warebee/frontend/data-access-api-graphql';
import {
  getOrderLinesWithJobsDataQuery,
  OrderLinesWithJobsDataRow,
  OrderLinesWithJobsSortBy,
  SimulationHqDataRow,
  SimulationHqFilterBy,
} from '@warebee/shared/dataset-queries';
import {
  DatasetQueryConverterConfig,
  DatasetQueryExportJobParams,
} from '@warebee/shared/export-converter';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { containsFilter } from '../common/filterHelper';
import { AsyncLoadStatus, DatasetDataViewProps } from '../common/types';
import DatasetTable from '../components/DatasetTable';
import useLoadOrderLinesWithJobsTableData from '../simulation/hooks/useLoadOrderLinesWithJobsTableData';
import {
  analyzeOrderLinesWithJobsTableData,
  analyzeOrderLinesWithJobsTableDataLoadStatus,
  analyzeOrderLinesWithJobsTableDataState,
  analyzeOrderLinesWithJobsTableTotalCount,
} from '../simulation/store/analyze.state';
import {
  simulationAnalyzeResult,
  simulationOrderSetId,
} from '../simulation/store/simulation.state';
import { warehouseSelectedId } from '../store/warehouse.state';
import useOrderLinesWithJobsDataTableConfig from './useOrderLinesWithJobsDataTableConfig';

const OrderLinesWithJobsDataTable: React.FC<
  DatasetDataViewProps<SimulationHqDataRow>
> = props => {
  const { t } = useTranslation('dataset');
  const warehouseId = useRecoilValue(warehouseSelectedId);
  const data = useRecoilValue(analyzeOrderLinesWithJobsTableData);
  const totalCount = useRecoilValue(analyzeOrderLinesWithJobsTableTotalCount);
  const status = useRecoilValue(analyzeOrderLinesWithJobsTableDataLoadStatus);
  const orderSetId = useRecoilValue(simulationOrderSetId);
  const analyzeResult = useRecoilValue(simulationAnalyzeResult);
  const [tableState, setTableState] = useRecoilState(
    analyzeOrderLinesWithJobsTableDataState,
  );
  const [runExportCSV] = useRunExportJobMutation();

  const [loadCallback, reset] = useLoadOrderLinesWithJobsTableData();
  const columnsConfig = useOrderLinesWithJobsDataTableConfig();

  const { searchValues, sortValues } = tableState;

  useEffect(() => {
    callDataLoad(tableState.searchValues, tableState.sortValues);
  }, [props.id, tableState, analyzeResult]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  function callDataLoad(
    searchValues: Record<string, string>,
    sortValues: Partial<Record<string, SortDirection>>,
    page = { isAppend: false, skip: 0 },
  ) {
    if (_.isNil(props.id)) return;

    const filterBy: SimulationHqFilterBy = _.reduce(
      _.entries(searchValues),
      (acc, [key, value]) => {
        return {
          ...acc,
          [key]: containsFilter(value),
        };
      },
      {} as SimulationHqFilterBy,
    );

    const sortBy: OrderLinesWithJobsSortBy[] = _.map(
      sortValues,
      (value, key, index) => ({
        field: key as any,
        direction: value,
      }),
    ).filter(i => !!i);

    loadCallback({
      orderSet: {
        datasetId: orderSetId,
      },
      picklistSet: {
        datasetId: analyzeResult.analyzedPicklistSet?.id,
      },

      isAppend: page.isAppend,
      page: {
        skip: page.skip,
      },
      filterBy,
      sortBy,
    });
  }

  function onLoadNext() {
    callDataLoad(tableState.searchValues, tableState.sortValues, {
      isAppend: true,
      skip: itemsCount,
    });
  }

  const converterConfig: DatasetQueryConverterConfig<OrderLinesWithJobsDataRow> =
    {
      columnsConfig,
    };

  const query = getOrderLinesWithJobsDataQuery({
    orderSet: {
      datasetId: orderSetId,
    },
    picklistSet: {
      datasetId: analyzeResult.analyzedPicklistSet?.id,
    },
  }).compile();

  async function startExportCSV() {
    const variables: DatasetQueryExportJobParams<OrderLinesWithJobsDataRow> = {
      converterId: 'DATASET_QUERY',
      query: QueryDatasetDocument.loc.source.body,
      config: converterConfig,
      variables: {
        warehouseId,
        query: query.sql,
        params: query.parameters,
      },
      filename: `analyzed-order-lines.csv`,
    };

    const { data, errors } = await runExportCSV({
      variables,
    });
    return {
      errors: errors,
      job: data.createExportJob,
    };
  }

  // const flattenData = getActivityFeedTableRows(
  //   data?.content as ActivityFeedEventDataset[],
  //   converterConfig,
  // );

  const isLoading = status === AsyncLoadStatus.Loading;
  const itemsCount = data?.length || 0;

  // if (props.customRenders) {
  //   _.forEach(props.customRenders, (renderer, key) => {
  //     const cfg = _.find(columnsConfig, c => c.field === key);
  //     if (!_.isNil(cfg)) {
  //       cfg.render = renderer;
  //     }
  //   });
  // }

  return (
    <DatasetTable
      id={props.id}
      data={data}
      columnsConfig={columnsConfig}
      keyFields={['orderKey', 'orderLine']}
      hasCounter
      hideScreenTitle={props.hideScreenTitle}
      subtitle={t`Activities`}
      title={props.title}
      isSticky
      onLoadNext={onLoadNext}
      onSearch={searchValues => {
        setTableState({ ...tableState, searchValues });
      }}
      onSort={sortValues => {
        setTableState({ ...tableState, sortValues });
      }}
      totalCount={totalCount}
      searchValues={searchValues}
      sortBy={sortValues}
      isLoading={isLoading}
      onStartExportClick={startExportCSV}
    />
  );
};

export default OrderLinesWithJobsDataTable;
